import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Modal, ModalHeader, ModalFooter, BodySmall, Select, Skeleton } from '@parspec/pixel';

import { DEFAULT_PRECISION, NO_ROUNDING, ROUNDING_BEHAVIOUR, ROUNDING_PRECISION } from './constants';
import { useGetBomRoundingPresetQuery, useUpdateBomRoundingMutation } from '../queries';

interface RoundingProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (roundingBehviour: string, roundingPrecision: string) => void;
}

const Rounding = (props: RoundingProps) => {
    const { bomId = '' } = useParams();

    const { open, onClose, onSuccess } = props;
    const [roundingBehviour, setRoundingBehviour] = useState<string>('');
    const [roundingPrecision, setRoundingPrecision] = useState<string>('');

    const { isLoading, data: bomRoundingPreset } = useGetBomRoundingPresetQuery(bomId!, {
        enabled: Boolean(bomId)
    });
    const { mutateAsync: updateRoundingData, isLoading: exportLoading } = useUpdateBomRoundingMutation();

    useEffect(() => {
        if (bomRoundingPreset?.data) {
            setRoundingBehviour(bomRoundingPreset?.data?.rounding_behaviour);
            setRoundingPrecision(bomRoundingPreset?.data?.rounding_precision);
        }
    }, [bomRoundingPreset?.data]);

    const handleSubmit = async () => {
        try {
            const roundingPrecisionForNoRound = roundingBehviour === NO_ROUNDING ? NO_ROUNDING : roundingPrecision;
            const data = {
                rounding_behaviour: roundingBehviour,
                rounding_precision: roundingPrecisionForNoRound,
                bomId: bomId!
            };
            onSuccess?.(roundingBehviour, roundingPrecisionForNoRound);
            await updateRoundingData(data);
        } catch (e) { }
    };

    const handleRoundingPrecision = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRoundingPrecision(event.target.value);
    };

    const handleRoundingBehviour = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const precision = roundingPrecision === NO_ROUNDING ? DEFAULT_PRECISION : roundingPrecision;
        setRoundingPrecision(precision);
        setRoundingBehviour(event.target.value);
    };

    const header = <ModalHeader title="Rounding Behavior" onClose={onClose} />;
    const footer = <ModalFooter onReject={onClose} onAccept={handleSubmit} continueButtonLabel="Confirm" isLoading={isLoading || exportLoading} />;
    return (
        <>
            <Modal header={header} footer={footer} open={open}>
                <Box width={'500px'}>
                    <BodySmall limit={false} color="text.secondary" mb={4}>
                        Select the rounding behavior for this bill of materials. The margin (%) will automatically recalculate to meet the set behavior.
                    </BodySmall>
                    <Box display={'flex'} gap={4}>
                        {isLoading ? (
                            <Skeleton width={250} height={36} variant="rectangular" />
                        ) : (
                            <Box width={250}>
                                <Select
                                    label={'Rounding Behavior'}
                                    options={ROUNDING_BEHAVIOUR}
                                    optionLabelKeyname="name"
                                    optionValueKeyname="name"
                                    onChange={handleRoundingBehviour}
                                    value={roundingBehviour}
                                />
                            </Box>
                        )}
                        {isLoading ? (
                            <Skeleton width={250} height={36} variant="rectangular" />
                        ) : (
                            <Box width={250}>
                                <Select
                                    label={'Rounding Precision'}
                                    options={ROUNDING_PRECISION}
                                    optionLabelKeyname="name"
                                    optionValueKeyname="id"
                                    onChange={handleRoundingPrecision}
                                    value={roundingBehviour === NO_ROUNDING ? '' : roundingPrecision}
                                    disabled={roundingBehviour === NO_ROUNDING}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};
export default Rounding;
